import React from 'react'

import {
  Button,
  capitalize,
  ConfirmationPopoverContent,
  Icon,
  Popover,
  type PopoverProps,
  ToastContent,
  useIsClient,
} from '../../module'
import { type ToastContentProps } from '../Toast/ToastContent'
import styles from './_alert.module.scss'
import { type ConfirmationItem } from '../Button/ButtonComponents/ConfirmationButton'
import { type IconColorList } from '../Icons/Icon.models'

export type AlertTypes = 'success' | 'error' | 'warning' | 'info'

type BaseAlertProps = ToastContentProps & {
  /** Optional class can be added */
  customClass?: string
  /** Optional prop to add a test id to the Alert for QA testing */
  qaTestId?: string
}

type AlertPropsWithoutCloseButton = BaseAlertProps & {
  close?: never
}

type AlertPropsWithCloseButton = BaseAlertProps & {
  close: {
    /** Close `Alert` function. */
    callout: () => void
    /** Hide or show the close button. */
    show: boolean
    /** Optionally change the behavior for the close button. If set to `confirmation`, then a confirmation will appear when the close button is clicked. */
    confirmation?: ConfirmationItem
  }
}

export type AlertProps =
  | AlertPropsWithoutCloseButton
  | AlertPropsWithCloseButton

const Alert = ({
  customClass = '',
  close,
  qaTestId = 'alert',
  ...rest
}: AlertProps): React.JSX.Element => {
  const { type } = rest,
    alertClassName = styles[`alert${capitalize(type)}`],
    isClient = useIsClient()

  let iconColor = 'dark-green'
  if (rest.type === 'error') iconColor = 'dark-red'
  if (rest.type === 'info') iconColor = 'dark-blue'
  if (rest.type === 'warning') iconColor = 'dark-yellow'

  const confirm = (close: () => void, callout?: () => void) => {
    close()
    callout?.()
  }

  const closeIcon = (
    <Icon icon='x' iconSize='12px' color={iconColor as IconColorList} />
  )
  const arrowColor =
    `dark-${close?.confirmation?.type}` as PopoverProps['arrowColor']

  return (
    <div
      className={`${styles.alert} ${alertClassName} ${customClass}`}
      qa-test-id={qaTestId}
    >
      <ToastContent {...rest} />
      {close?.show &&
        (close.confirmation ? (
          <Popover
            position='bottom'
            className={styles.confirmationContainer}
            noPadding
            arrowColor={arrowColor}
            {...(isClient ? { appendTo: document.body } : {})}
            popoverContent={({ setVisible }) => {
              const closePopover = () => setVisible(false)
              return close.confirmation ? (
                <ConfirmationPopoverContent
                  body={close.confirmation.body}
                  header={close.confirmation.header}
                  type={close.confirmation.type ?? 'green'}
                  cancelCallout={closePopover}
                  confirmCallout={() =>
                    confirm(closePopover, close.confirmation?.confirmCallout)
                  }
                  confirmButtonText={close.confirmation.confirmButtonText}
                />
              ) : (
                <></>
              )
            }}
          >
            {({ setVisible }) => (
              <Button as='unstyled' onClick={() => setVisible(true)}>
                {closeIcon}
              </Button>
            )}
          </Popover>
        ) : (
          <Button as='unstyled' onClick={close.callout}>
            {closeIcon}
          </Button>
        ))}
    </div>
  )
}

export default Alert
